import { getTenantBackendUrlAndTenant } from "@axios/utils";

import type { ChatTranscriptMessage } from "./messages";

export type ChatSource = {
  object_name?: string;
  h1?: string;
  h2?: string;
  h3?: string;
  start_index?: number;
};

export type ChatMessage = ChatTranscriptMessage & {
  sources?: ChatSource[];
};

export type ChatConversation = {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
};

export type ChatSuccess = {
  answer: string;
  sources: ChatSource[];
  conversationId?: string;
};

export type ChatPage<T> = {
  items: T[];
  nextCursor: string | null;
};

export class ChatRequestError extends Error {
  status: number;
  retryable: boolean;

  constructor(message: string, status: number, retryable: boolean) {
    super(message);
    this.status = status;
    this.retryable = retryable;
  }
}

function chatContext(): { token: string; tenant: string; branch: string } {
  const subdomain =
    typeof window !== "undefined" ? window.location.hostname.split(".")[0] : "";
  const { tenant } = getTenantBackendUrlAndTenant(subdomain);
  return {
    token: localStorage.getItem("access_token") || "",
    tenant,
    branch: localStorage.getItem("branch_id") || "",
  };
}

function redirectToLogin() {
  document.cookie =
    "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  document.cookie =
    "user_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  localStorage.removeItem("access_token");
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

function detailMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== "object") return fallback;
  const detail = (payload as { detail?: unknown }).detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return fallback;
}

function errorForStatus(status: number, payload: unknown): ChatRequestError {
  if (status === 401) return new ChatRequestError("Session over.", 401, false);
  if (status === 400 || status === 403 || status === 404) {
    return new ChatRequestError(
      detailMessage(payload, "Request was rejected."),
      status,
      false,
    );
  }
  return new ChatRequestError(
    "something went wrong",
    status,
    status === 503,
  );
}

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const { token, tenant, branch } = chatContext();
  if (!token) {
    redirectToLogin();
    throw new ChatRequestError("Session over.", 401, false);
  }

  const run = async () => {
    const res = await fetch(path, {
      ...init,
      credentials: "omit",
      headers: {
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        Authorization: `Bearer ${token}`,
        tenant,
        branch,
      },
    });
    const payload = await res.json().catch(() => null);
    if (res.status === 401) redirectToLogin();
    if (!res.ok) throw errorForStatus(res.status, payload);
    return payload;
  };

  try {
    return await run();
  } catch (error) {
    if (error instanceof ChatRequestError && error.status === 503) {
      await new Promise((resolve) => setTimeout(resolve, 800));
      return run();
    }
    throw error;
  }
}

function nextCursor(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const cursor = (payload as { next_cursor?: unknown }).next_cursor;
  return typeof cursor === "string" && cursor ? cursor : null;
}

export async function postChat(
  question: string,
  conversationId?: string | null,
): Promise<ChatSuccess> {
  const body: { question: string; conversation_id?: string } = { question };
  if (conversationId) body.conversation_id = conversationId;

  const payload = await request("/api/chat", {
    method: "POST",
    body: JSON.stringify(body),
  });
  const record =
    payload && typeof payload === "object"
      ? (payload as {
          answer?: unknown;
          sources?: unknown;
          conversation_id?: unknown;
        })
      : {};
  return {
    answer: typeof record.answer === "string" ? record.answer : "",
    sources: Array.isArray(record.sources) ? record.sources : [],
    conversationId:
      typeof record.conversation_id === "string"
        ? record.conversation_id
        : undefined,
  };
}

export async function listConversations(
  cursor?: string | null,
): Promise<ChatPage<ChatConversation>> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  const payload = await request(
    `/api/chat/conversations${qs ? `?${qs}` : ""}`,
  );
  const rows =
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { conversations?: unknown }).conversations)
      ? (payload as { conversations: unknown[] }).conversations
      : [];
  const items = rows.flatMap((row): ChatConversation[] => {
    if (!row || typeof row !== "object") return [];
    const item = row as {
      id?: unknown;
      title?: unknown;
      created_at?: unknown;
      updated_at?: unknown;
    };
    if (typeof item.id !== "string") return [];
    return [
      {
        id: item.id,
        title: typeof item.title === "string" ? item.title : null,
        created_at: typeof item.created_at === "string" ? item.created_at : "",
        updated_at: typeof item.updated_at === "string" ? item.updated_at : "",
      },
    ];
  });
  return { items, nextCursor: nextCursor(payload) };
}

export async function listMessages(
  conversationId: string,
  cursor?: string | null,
): Promise<ChatPage<ChatMessage>> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  const payload = await request(
    `/api/chat/conversations/${conversationId}/messages${qs ? `?${qs}` : ""}`,
  );
  const rows =
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { messages?: unknown }).messages)
      ? (payload as { messages: unknown[] }).messages
      : [];
  const items = rows.flatMap((row): ChatMessage[] => {
    if (!row || typeof row !== "object") return [];
    const item = row as { id?: unknown; role?: unknown; content?: unknown };
    if (item.role !== "user" && item.role !== "assistant") return [];
    if (typeof item.content !== "string") return [];
    return [
      {
        id: typeof item.id === "string" ? item.id : undefined,
        role: item.role,
        content: item.content,
      },
    ];
  });
  return { items, nextCursor: nextCursor(payload) };
}
